// third party imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Select, message } from "antd";
import { X, ChevronDown } from "react-feather";

// internal imports
import "./stylesSettleBetPopup.css";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
  handleExpiredTokenError,
} from "../../../../../utils/ErrorUtils";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatCurrency } from "../../../../../utils/financialFormatters";

const { Option } = Select;

const SETTLEMENT_STATUS_OPTIONS = [
  {
    value: "unsettled",
    label: "Unsettled",
    color: "#f59e0b",
    textColor: "#000",
  },
  { value: "won", label: "Won", color: "#22c55e", textColor: "#fff" },
  { value: "lost", label: "Lost", color: "#ef4444", textColor: "#fff" },
  { value: "void", label: "Void", color: "#6b7280", textColor: "#fff" },
];

// Status mapping: won=1, lost=2, void=4, unsettled=5
const STATUS_MAP = {
  won: 1,
  lost: 2,
  void: 4,
  unsettled: 5,
};

const SettleBetPopup = ({
  onClose,
  betData,
  refreshBets,
  currencySymbol,
}) => {
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const [legs, setLegs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLegStatus = (leg) => {
    if (!leg.finalized) return "unsettled";
    if (leg.void_factor != null && leg.void_factor !== "") {
      const voidFactor = Number(leg.void_factor);
      if (voidFactor === 0 || voidFactor === 1) return "void";
    }
    if (leg.won === "1") return "won";
    if (leg.won === "-1") return "unsettled";
    if (leg.won === "0") return "lost";
  };

  // Initialize legs from bet data
  useEffect(() => {
    if (betData?.bets) {
      setLegs(
        betData.bets.map((leg, index) => ({
          void_factor: leg.void_factor || null,
          market: leg.selection.market.market_name || "Market",
          selection: leg.selection.market.outcome[0].name || "Selection",
          status: handleLegStatus(betData?.bets[index]),
          bet_index: index,
          finalized: leg.finalized || false,
          is_round_robin: betData.bet_type === "4",
        }))
      );
    } else {
      // Create legs from selections if legs not provided
      setLegs([{
        market: betData?.market || "Market",
        void_factor: betData?.void_factor || null,
        selection: betData?.selection || "Selection",
        status: handleLegStatus(betData),
        bet_index: betData?.bet_index || 0,
        finalized: betData?.finalized || false,
        is_round_robin: false,
      }]
      );
    }
  }, [betData]);

  const handleStatusChange = (legIndex, newStatus) => {
    setLegs((prevLegs) =>
      prevLegs.map((leg, idx) =>
        idx === legIndex ? { ...leg, status: newStatus } : leg
      )
    );
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      // Create individual payloads for each leg
      const legPayloads = legs.map((leg) => {
        const payload = {
          bet_id: betData?.bet_id,
          bet_index: leg.bet_index,
          status: STATUS_MAP[leg.status] || STATUS_MAP.unsettled,
          finalized: leg.finalized,
        };
        // Add void_factor when status is void
        if (leg.status === "void") {
          payload.void_factor = leg.void_factor;
        }
        return payload;
      });

      // Call the endpoint for each leg (in succession)
      let response;
      for (const payload of legPayloads) {
        // Skip unsettled legs (status 5)
        if (payload.status === 5 || payload.finalized === "1") continue;

        // Remove finalized from the payload
        const { finalized, ...payloadWithoutFinalized } = payload;

        response = await PrivateAdminPanelInstance.post(
          betData?.bet_type === "4" ? API_ROUTES?.SETTLE_ROUND_ROBIN_BET : API_ROUTES?.SETTLE_PARLAY_BET,
          payloadWithoutFinalized
        );
      }

      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
        )
      );
      refreshBets?.();
      onClose();
    } catch (error) {
      console.error("Error settling bet:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
      );
      handleExpiredTokenError(navigate, error?.response);
    } finally {
      setIsSubmitting(false);
    }
  };

  const setLegStatus = (index) => {
    if (!legs[index]) return "unsettled";
    return legs[index].status || "unsettled";
  };

  return (
    <div className="bet-table-settle-bet-overlay">
      <div className="bet-table-settle-bet-popup">
        {/* Header */}
        <div className="bet-table-settle-bet-header">
          <h2 className="bet-table-settle-bet-title">{AppStrings.SETTLE_BET}</h2>
          <button className="bet-table-settle-close-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Bet ID */}
        <div className="bet-table-settle-bet-id-row">
          <span className="bet-table-settle-bet-id-label">{AppStrings.BET_ID}</span>
          <span className="bet-table-settle-bet-id-value">{betData?.bet_id || "-"}</span>
        </div>

        {legs?.map((bet, idx) => {
          return (
            <div className="bet-table-settle-legs-container" key={idx}>
              <div className={`bet-table-settle-leg-row ${bet.finalized === "1" ? "bet-table-settle-leg-row-finalized" : ""}`}>
                <div className="bet-table-settle-leg-info">
                  <div className="bet-table-settle-leg-details">
                    <span className="bet-table-settle-leg-market">{bet.market}</span>
                    <span className="bet-table-settle-leg-selection">
                      {bet.selection}
                    </span>
                  </div>
                </div>
                <div className="bet-table-settle-leg-status">
                  <span className="bet-table-settle-status-label">
                    {AppStrings.SETTLEMENT_STATUS}
                  </span>
                  <Select
                    value={bet.status}
                    onChange={(value) => handleStatusChange(idx, value)}
                    className="bet-table-settle-status-select"
                    popupClassName="bet-table-settle-status-dropdown"
                    suffixIcon={<ChevronDown size={16} color="#fff" />}
                    disabled={bet.finalized === "1"}
                  >
                    {SETTLEMENT_STATUS_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        <div className="bet-table-settle-status-option-wrapper">
                          <span
                            className="bet-table-settle-status-option-badge"
                            style={{
                              backgroundColor: option.color,
                              color: option.textColor,
                            }}
                          >
                            {option.label}
                          </span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          )
        })}

        {/* Settlements Container */}

        <div className="bet-table-settle-settlements-container" style={{ display: legs[0]?.is_round_robin ? "flex" : "none" }}>
          <div className="bet-table-settle-settlements-header">
            <span className="bet-table-settle-settlements-header-picks">Picks</span>
            <span className="bet-table-settle-settlements-header-to-pay">To Pay</span>
          </div>
          {Object.entries(betData?.settlements || {}).map(([key, settlement]) => {
            return (
              <div key={key} className="bet-table-settle-settlements-row">
                <div className="bet-table-settle-settlements-picks">
                  <div className="bet-table-settle-settlements-picks-list">{settlement.comb_idx.map(idx => {
                    return (
                      <span key={idx} className={`bet-table-settle-settlements-picks-list-item ${setLegStatus(idx)}`}>{parseInt(idx) + 1}</span>
                    )
                  })}</div>
                </div>
                <div className="bet-table-settle-settlements-to-pay">
                  <span className="bet-table-settle-settlements-to-pay-amount">
                    {formatCurrency(
                      Number(settlement.possible_payout),
                      currencySymbol,
                      2,
                    )}
                  </span>
                </div>
              </div>
            )
          })}
          <div className="bet-table-settle-settlements-status-info">
            <div className="bet-table-settle-settlements-status-info-item">
              <span className="bet-table-settle-settlements-status-info-icon unsettled"></span>
              <span>Unsettled</span>
            </div>
            <div className="bet-table-settle-settlements-status-info-item">
              <span className="bet-table-settle-settlements-status-info-icon won"></span>
              <span>Won</span>
            </div>

            <div className="bet-table-settle-settlements-status-info-item">
              <span className="bet-table-settle-settlements-status-info-icon live"></span>
              <span>Live</span>
            </div>
            <div className="bet-table-settle-settlements-status-info-item">
              <span className="bet-table-settle-settlements-status-info-icon lost"></span>
              <span>Lost</span>
            </div>
            <div className="bet-table-settle-settlements-status-info-item">
              <span className="bet-table-settle-settlements-status-info-icon pushed"></span>
              <span>Pushed</span>
            </div>
          </div>
        </div>


        {/* Actions */}
        <div className="bet-table-settle-bet-actions">
          <button
            className="bet-table-settle-cancel-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="bet-table-settle-save-button"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettleBetPopup;
